<script lang="ts" setup>
import { ref, computed, watch, onMounted, toRaw } from 'vue'

import IconBack from '@lahaus-roomie/roomie/src/assets/icons/v2/back.svg'

import type { Props } from './types'
import type { SearchFilters } from '@/components/SearchFilters/types'
import { getLocationTitle } from '@/utils/locations'

import { LazyAppModal } from '#components'
import OnboardingStepsOpportunityType from '@/components/BrandedV2/OnboardingSteps/OpportunityType/index.vue'
import OnboardingStepsDeliveryTerm from '@/components/BrandedV2/OnboardingSteps/DeliveryTerm/index.vue'
import OnboardingStepsLoader from '@/components/BrandedV2/OnboardingSteps/Loader/index.vue'
import OnboardingStepsBedroomsNumber from '@/components/BrandedV2/OnboardingSteps/BedroomsNumber/index.vue'
import OnboardingStepsIndicator from '@/components/BrandedV2/OnboardingSteps/Indicator/index.vue'

const ANSWERS_COOKIE_MAX_AGE = 60 * 60 * 24 * 30

const props = defineProps<Props>()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const { $trackPage } = useNuxtApp()

const currentStepIndex = ref<number>(parseInt(route.query.step as string))
const currentFilters = ref<SearchFilters>({ ...props.initialFilters })
const loading = ref(false)

const hasCompletedOnboarding = useCookie<boolean>('has_completed_onboarding', { maxAge: ANSWERS_COOKIE_MAX_AGE })
const opportunityTypeCookie = useCookie<string | null>('onboarding_opportunity_type', { maxAge: ANSWERS_COOKIE_MAX_AGE })
const deliveryTermCookie = useCookie<string | null>('onboarding_delivery_term', { maxAge: ANSWERS_COOKIE_MAX_AGE })
const bedroomsNumberCookie = useCookie<number | null>('onboarding_bedrooms_number', { maxAge: ANSWERS_COOKIE_MAX_AGE })

const locationsTitle = computed(() => {
  return getLocationTitle(route.query.custom_title, toRaw(currentFilters.value), t, locale.value)
})

const syncStepQueryParam = async () => {
  await router.replace({ query: { ...route.query, step: currentStepIndex.value.toString() } })

  // @ts-ignore
  $trackPage()
}

const syncFiltersQueryParams = async () => {
  const nextQueryParams: any = { ...route.query }

  if (currentFilters.value.clusters?.length) {
    const value = currentFilters.value.clusters.join('~')
    nextQueryParams.clusters = value
  }

  nextQueryParams.opportunity_type = currentFilters.value.opportunityType ?? null
  nextQueryParams.delivery_term = currentFilters.value.deliveryTerm ?? null
  nextQueryParams.bedrooms_number = currentFilters.value.bedroomsNumber ? currentFilters.value.bedroomsNumber.toString() : null

  for (const key in nextQueryParams) {
    if (!nextQueryParams[key]) {
      delete nextQueryParams[key]
    }
  }

  opportunityTypeCookie.value = currentFilters.value.opportunityType
  deliveryTermCookie.value = currentFilters.value.deliveryTerm
  bedroomsNumberCookie.value = currentFilters.value.bedroomsNumber

  await router.replace({ query: nextQueryParams })
}

const handleStepSubmit = async ({ name, value }: { name: string, value: string }) => {
  (currentFilters.value as any)[name] = value
  await syncFiltersQueryParams()
  currentStepIndex.value += 1
  await syncStepQueryParam()
}

const goToPreviousStep = async () => {
  currentStepIndex.value -= 1
  await syncStepQueryParam()
}

const handleSkip = async ({ name, value }: { name: string; value: string | null }) => {
  currentStepIndex.value += 1
  currentFilters.value[name] = value
  await syncFiltersQueryParams()
  await syncStepQueryParam()
}

const isOpportunityVisible = computed(() => currentStepIndex.value === 1)

const isDeliveryTermVisible = computed(() => currentStepIndex.value === 2)

const isBedroomsNumberVisible = computed(() => {
  return (currentFilters.value.opportunityType === 'investing' || !currentFilters.value.opportunityType)
    ? false
    : currentStepIndex.value === 3
})

const isReturnButtonVisible = computed(() => currentStepIndex.value > 1 && !loading.value)

const goToSearchPage = () => {
  loading.value = true
  navigateTo({
    path: '/venta/propiedades',
    query: { ...route.query }
  }, { external: true })
}

const dataAttributes = computed(() => {
  return {
    dataLh: {
      P: props.screen,
      A: 'seleccionar'
    }
  }
})

watch(() => props.initialFilters, (newInitialFilters) => {
  currentFilters.value = { ...newInitialFilters }
}, { deep: true })

watch(() => currentStepIndex.value, (nextStep) => {
  if (nextStep < 3) return

  const isInvestingFlow = currentFilters.value.opportunityType === 'investing' || !currentFilters.value.opportunityType
  const isLivingFlow = currentFilters.value.opportunityType === 'living'

  if (isInvestingFlow && nextStep === 3) {
    hasCompletedOnboarding.value = true
    return goToSearchPage()
  }

  if (isLivingFlow && nextStep === 4) {
    hasCompletedOnboarding.value = true
    return goToSearchPage()
  }
}, { immediate: true })

onMounted(async () => {
  const initialQueryParams: any = {}

  if (!route.query.step) {
    currentStepIndex.value = 1
    initialQueryParams.step = 1
  } else {
    currentStepIndex.value = parseInt(route.query.step as string) || 1
  }

  await router.replace({ query: { ...route.query, ...initialQueryParams } })
})
</script>

<script lang="ts">
export default {
  name: 'OnboardingStepsModal'
}
</script>

<template>
  <section class="branded-onboarding-modal w-full overflow-hidden absolute top-0 left-0">
    <div class="branded-onboarding-modal__bg w-[1980px] h-[950px]" />

    <LazyAppModal
      v-if="!loading">
      <button
        v-if="isReturnButtonVisible"
        id="branded-onboarding-modal-return-button"
        v-lh-id="{ ...dataAttributes.dataLh, Z: 'modal filtros', C: 'regresar' }"
        type="button"
        :aria-label="t('ariaLabelReturn')"
        class="absolute top-6 left-6 w-32 h-32 p-4 rounded-full"
        @click="goToPreviousStep">
        <IconBack class="pointer-events-none text-pine-600" />
      </button>

      <div class="branded-onboarding-modal-title w-full text-22 mt-16 text-center truncate">
        <p class="hl-2-m !text-18 text-pine-600">
          {{ t('title') }}
        </p>

        <p
          class="hl-1-sb mb-24 !text-24 text-pine-600 text-center truncate"
          :title="locationsTitle">
          {{ locationsTitle }}
        </p>
      </div>

      <OnboardingStepsIndicator :step="currentStepIndex" />

      <div>
        <OnboardingStepsOpportunityType
          v-if="isOpportunityVisible"
          :value="currentFilters.opportunityType"
          :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, Z: 'que_estas_buscando' }}"
          @submit="handleStepSubmit"
          @skip="handleSkip" />

        <OnboardingStepsDeliveryTerm
          v-if="isDeliveryTermVisible"
          :value="currentFilters.deliveryTerm"
          :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, Z: 'a_que_plazo_buscas' }}"
          @submit="handleStepSubmit"
          @skip="handleSkip" />

        <OnboardingStepsBedroomsNumber
          v-if="isBedroomsNumberVisible"
          :value="currentFilters.bedroomsNumber"
          :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, Z: 'cuantas_habitaciones_buscas' }}"
          @submit="handleStepSubmit"
          @skip="handleSkip" />
      </div>
    </LazyAppModal>

    <OnboardingStepsLoader
      v-if="loading" />
  </section>
</template>

<style lang="scss">
.branded-onboarding-modal {
  &__bg {
    background: url('https://media.lahaus.com/statics/static/images/onboarding-bg-mobile.png?width=375=&height=792');

    @screen md {
      background: url('https://media.lahaus.com/statics/static/images/onboarding-bg-desktop.png?width=1980=&height=950');
    }
  }

  & .app-modal-content {
    @apply max-w-374 rounded-16;

    position: absolute;
    top: 10%;

    @screen md {
      top: 20%;
    }

    & #roomie-modal-navigation-button {
      @apply hidden;
    }
  }

  & .roomie-overlay {
    @apply backdrop-blur-sm bg-opacity-40;
  }

  & .roomie-button  {
    @apply font-medium;
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
